import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { tasksAPI, modulesAPI, usersAPI, restaurantsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronRight, Plus, Link as LinkIcon, Paperclip, Send, CheckSquare, Building, MessageSquare, CalendarClock, GripVertical, Clock, Copy, User } from 'lucide-react';
import { toast } from 'sonner';
import DevTaskModal from '../components/DevTaskModal';
import { TASK_TYPE_COLORS, TASK_TYPE_LABELS, TASK_TYPE_ICONS, TASK_TYPE_OPTIONS } from '../lib/taskTypes';
import { DEV_STATUSES } from '../lib/taskStatuses';
import { DescriptionMediaStrip } from '../components/DescriptionMedia';
import { getDaysInProgress } from '../lib/format';

const COLUMNS = DEV_STATUSES.map(s => ({ status: s.value, label: s.label }));

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
export const PRIORITY_COLORS = { high: '#DC2626', medium: '#F59E0B', low: '#0EA5E9' };
const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };

// Return urgency style based on days until deadline. Done tasks get no urgency.
function getDeadlineUrgency(deadline, status) {
  if (!deadline || status === 'done') return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(deadline + 'T00:00:00');
  const diffMs = target.getTime() - now.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) return { level: 'overdue', bg: '#FEE2E2', border: '#F87171', text: '#B91C1C', label: `Просрочено ${Math.abs(days)} дн.` };
  if (days < 1) return { level: 'urgent', bg: '#FEE2E2', border: '#F87171', text: '#B91C1C', label: 'Сегодня' };
  if (days < 3) return { level: 'soon', bg: '#FEF3C7', border: '#FBBF24', text: '#92400E', label: `${days} дн.` };
  return { level: 'ok', bg: null, border: null, text: '#666666', label: `${days} дн.` };
}

function formatDeadline(deadline) {
  if (!deadline) return '';
  const d = new Date(deadline + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
}

function TaskCard({ task, onClick, onDragStart, onCopyLink }) {
  const type = task.task_type || 'feature';
  const TypeIcon = TASK_TYPE_ICONS[type] || TASK_TYPE_ICONS.feature;
  const urgency = getDeadlineUrgency(task.deadline, task.status);
  const age = getDaysInProgress(task);
  const cardStyle = urgency && urgency.bg ? {
    backgroundColor: urgency.bg,
    borderColor: urgency.border,
  } : {};
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onClick={() => onClick(task)}
      className={`border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${urgency && urgency.bg ? '' : 'bg-white border-[#E0E0E0]'}`}
      style={cardStyle}
      data-testid={`dev-task-card-${task.id}`}
    >
      {/* Type indicator bar */}
      <div className="w-full h-1" style={{ backgroundColor: TASK_TYPE_COLORS[type] || TASK_TYPE_COLORS.feature }} data-testid={`task-type-bar-${task.id}`} />
      <div className="p-3">
        {task.task_key && (
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1">
              <span className="font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#101010] text-[#CFFF40]" data-testid={`task-key-${task.id}`}>
                {task.task_key}
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCopyLink(task); }}
                title="Copy link to task"
                className="p-0.5 rounded text-[#B0B0B0] hover:text-[#101010] hover:bg-[#F0F0F0]"
                data-testid={`copy-task-link-${task.id}`}
              >
                <Copy size={12} />
              </button>
            </div>
            <div className="flex items-center gap-1">
              {task.source === 'telegram' && <Send size={10} className="text-[#999999]" title="Created via Telegram" />}
              {task.source === 'restaurant' && <Building size={10} className="text-[#999999]" title="Created by restaurant" />}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mb-1">
          <TypeIcon size={11} style={{ color: TASK_TYPE_COLORS[type] }} />
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: TASK_TYPE_COLORS[type] }} data-testid={`task-type-label-${task.id}`}>
            {TASK_TYPE_LABELS[type]}
          </span>
          <span className="text-[10px] text-[#D0D0D0]">•</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: PRIORITY_COLORS[task.priority] }} data-testid={`task-priority-label-${task.id}`}>
            {PRIORITY_LABELS[task.priority] || task.priority}
          </span>
          {!task.task_key && task.source === 'telegram' && <Send size={10} className="text-[#999999]" title="Created via Telegram" />}
          {!task.task_key && task.source === 'restaurant' && <Building size={10} className="text-[#999999]" title="Created by restaurant" />}
        </div>
        <p className="text-sm font-medium text-[#111111] line-clamp-2">{task.title}</p>
        {task.description && (
          <p className="text-xs text-[#666666] mt-1 line-clamp-2 whitespace-pre-line">{task.description}</p>
        )}
        <DescriptionMediaStrip media={task.description_media} />
        {task.restaurant_name && (
          <p className="text-[11px] text-[#999999] mt-1 flex items-center gap-1">
            <Building size={11} />
            <span className="truncate">{task.restaurant_name}</span>
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {age && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
              style={{ backgroundColor: age.frozen ? '#ECFDF5' : '#F5F5F5', color: age.frozen ? '#047857' : '#666666' }}
              title={age.frozen ? 'Days in progress (stopped in Done)' : 'Days in progress'}
              data-testid={`task-age-${task.id}`}
            >
              <Clock size={11} />
              <span>{age.days}d</span>
            </div>
          )}
          {task.deadline && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
              style={{
                backgroundColor: urgency && urgency.bg ? '#FFFFFF80' : '#F5F5F5',
                color: urgency ? urgency.text : '#666666',
              }}
              data-testid={`task-deadline-${task.id}`}
            >
              <CalendarClock size={11} />
              <span>{formatDeadline(task.deadline)}</span>
              {urgency && urgency.level !== 'ok' && (
                <span className="opacity-80">• {urgency.label}</span>
              )}
            </div>
          )}
          {task.author_name && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#F5F5F5] text-[#666666]"
              title="Author (who created the task)"
              data-testid={`task-author-${task.id}`}
            >
              <User size={11} />
              <span className="truncate max-w-[90px]">{task.author_name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E0E0E0]">
          {task.assignees?.length > 0 ? (
            <div className="flex items-center gap-1.5">
              <div className="flex -space-x-1">
                {task.assignees.slice(0, 3).map(a => (
                  <div key={a.id} className="w-5 h-5 rounded-full bg-[#FFFF66] text-[#101010] flex items-center justify-center text-[10px] font-bold border-2 border-white" title={a.name}>
                    {a.initials}
                  </div>
                ))}
                {task.assignees.length > 3 && (
                  <div className="w-5 h-5 rounded-full bg-[#E0E0E0] text-[#666666] flex items-center justify-center text-[9px] font-bold border-2 border-white">
                    +{task.assignees.length - 3}
                  </div>
                )}
              </div>
              <span className="text-xs text-[#666666] truncate max-w-[90px]">{task.assignee_name}</span>
            </div>
          ) : (
            <span className="text-xs text-[#999999]">Unassigned</span>
          )}
          <div className="flex items-center gap-2 text-[#666666]">
            {task.checklist?.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs" data-testid={`checklist-progress-${task.id}`}>
                <CheckSquare size={12} />
                {task.checklist.filter(c => c.done).length}/{task.checklist.length}
              </span>
            )}
            {task.comment_count > 0 && (
              <span className="flex items-center gap-0.5 text-xs" title="Comments">
                <MessageSquare size={12} />
                {task.comment_count}
              </span>
            )}
            {task.link && (
              <a href={task.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={task.link}>
                <LinkIcon size={12} className="hover:text-[#FF6B35]" />
              </a>
            )}
            {task.attachments?.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs">
                <Paperclip size={12} />
                {task.attachments.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [collapsedCols, setCollapsedCols] = useState({});
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [filterRequestedBy, setFilterRequestedBy] = useState('');
  const [filterType, setFilterType] = useState('');
  const [modalTask, setModalTask] = useState(undefined); // undefined = closed, null = create, object = edit
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedModuleId, setDraggedModuleId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  const handleCopyLink = useCallback(async (task) => {
    const url = `${window.location.origin}/tasks?task=${task.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка на задачу скопирована');
    } catch (err) {
      toast.error('Не удалось скопировать ссылку');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, modulesRes, usersRes, restaurantsRes] = await Promise.all([
        tasksAPI.getAll(),
        modulesAPI.getAll(),
        isAdmin ? usersAPI.getAll() : Promise.resolve({ data: [] }),
        isAdmin ? restaurantsAPI.getAll() : Promise.resolve({ data: [] }),
      ]);
      setTasks(tasksRes.data);
      setModules(modulesRes.data);
      setUsers(usersRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Open a task directly from a shared link (/tasks?task=<id>)
  useEffect(() => {
    const tid = searchParams.get('task');
    if (!tid || loading || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const found = tasks.find(t => t.id === tid);
    if (found) {
      setModalTask(found);
    } else {
      tasksAPI.getOne(tid).then(res => setModalTask(res.data)).catch(() => toast.error('Задача не найдена или нет доступа'));
    }
  }, [searchParams, tasks, loading]);

  const clearTaskParam = useCallback(() => {
    if (searchParams.get('task')) {
      searchParams.delete('task');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const requestedByOptions = useMemo(() => {
    const set = new Set();
    tasks.forEach(t => { if (t.requested_by) set.add(t.requested_by); });
    return Array.from(set).sort();
  }, [tasks]);

  const filtered = useMemo(() => tasks.filter(t => {
    if (filterAssignee && !(t.assignee_ids || []).includes(filterAssignee)) return false;
    if (filterRestaurant && t.restaurant_id !== filterRestaurant) return false;
    if (filterRequestedBy && t.requested_by !== filterRequestedBy) return false;
    if (filterType && (t.task_type || 'feature') !== filterType) return false;
    return true;
  }), [tasks, filterAssignee, filterRestaurant, filterRequestedBy, filterType]);

  const groups = useMemo(() => {
    const byModule = {};
    filtered.forEach(t => {
      const key = t.module_id && t.module_name ? t.module_id : 'other';
      if (!byModule[key]) byModule[key] = { id: key, name: t.module_name || 'Other', tasks: [] };
      byModule[key].tasks.push(t);
    });
    const ordered = [];
    modules.forEach(m => {
      if (byModule[m.id]) {
        ordered.push(byModule[m.id]);
        delete byModule[m.id];
      }
    });
    Object.values(byModule).forEach(g => {
      if (g.id !== 'other') ordered.push(g);
    });
    if (byModule.other) ordered.push(byModule.other);
    return ordered;
  }, [filtered, modules]);

  const assignableUsers = users.filter(u => ['admin', 'developer'].includes(u.role));

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedModuleId) return;
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }
    try {
      await tasksAPI.update(draggedTask.id, { status: newStatus });
      toast.success('Task moved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
    setDraggedTask(null);
  };

  const handleModuleDrop = async (e, targetModuleId) => {
    e.preventDefault();
    const sourceId = draggedModuleId;
    setDraggedModuleId(null);
    if (!sourceId || sourceId === targetModuleId) return;
    const ids = modules.map(m => m.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetModuleId);
    if (from < 0 || to < 0) return;
    const nextIds = [...ids];
    const [moved] = nextIds.splice(from, 1);
    nextIds.splice(to, 0, moved);
    const moduleMap = Object.fromEntries(modules.map(m => [m.id, m]));
    setModules(nextIds.map(id => moduleMap[id]));
    try {
      await modulesAPI.reorder(nextIds);
    } catch (err) {
      toast.error('Failed to save module order');
      fetchData();
    }
  };

  const toggleColumn = (groupId, status) => {
    const key = `${groupId}:${status}`;
    setCollapsedCols(prev => ({ ...prev, [key]: !(prev[key] ?? status === 'done') }));
  };

  const isColumnCollapsed = (groupId, status) => collapsedCols[`${groupId}:${status}`] ?? status === 'done';

  const clearFilters = () => {
    setFilterAssignee('');
    setFilterRestaurant('');
    setFilterRequestedBy('');
    setFilterType('');
  };

  const hasActiveFilters = filterAssignee || filterRestaurant || filterRequestedBy || filterType;

  if (loading) {
    return (
      <DashboardLayout pageTitle="Tasks">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Tasks">
      <div className="space-y-4" data-testid="tasks-page">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 bg-white border border-[#E0E0E0] rounded-lg p-3">
          {isAdmin && (
            <>
              <select
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
                className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
                data-testid="filter-assignee"
              >
                <option value="">All Assignees</option>
                {assignableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <select
                value={filterRestaurant}
                onChange={(e) => setFilterRestaurant(e.target.value)}
                className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
                data-testid="filter-restaurant"
              >
                <option value="">All Restaurants</option>
                {restaurants.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <select
                value={filterRequestedBy}
                onChange={(e) => setFilterRequestedBy(e.target.value)}
                className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
                data-testid="filter-requested-by"
              >
                <option value="">All Requesters</option>
                {requestedByOptions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
                data-testid="filter-task-type"
              >
                <option value="">All Types</option>
                {TASK_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  Clear
                </Button>
              )}
            </>
          )}
          <span className="text-sm text-[#666666]">{filtered.length} tasks</span>
          <div className="flex-1 hidden sm:block"></div>
          {isAdmin && (
            <Button
              onClick={() => setModalTask(null)}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
              data-testid="new-task-btn"
            >
              <Plus size={16} className="mr-1" /> New Task
            </Button>
          )}
        </div>

        {/* Module groups */}
        {groups.length === 0 && (
          <div className="bg-white border border-[#E0E0E0] rounded-lg p-10 text-center text-[#666666]" data-testid="tasks-empty-state">
            No tasks yet. {isAdmin ? 'Create one with "New Task" or via the Telegram bot.' : 'Tasks assigned to you will appear here.'}
          </div>
        )}

        {groups.map(group => {
          const isCollapsed = collapsed[group.id];
          const isReorderable = isAdmin && group.id !== 'other';
          return (
            <div
              key={group.id}
              className={`bg-white border rounded-lg transition-colors ${draggedModuleId && draggedModuleId !== group.id ? 'border-dashed border-[#FF6B35]' : 'border-[#E0E0E0]'}`}
              onDragOver={(e) => { if (draggedModuleId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
              onDrop={(e) => { if (draggedModuleId) handleModuleDrop(e, group.id); }}
              data-testid={`module-group-${group.id}`}
            >
              <div
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#F5F5F5] rounded-t-lg transition-colors"
                draggable={isReorderable}
                onDragStart={(e) => {
                  if (!isReorderable) return;
                  setDraggedModuleId(group.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => setDraggedModuleId(null)}
              >
                {isReorderable && (
                  <GripVertical size={16} className="text-[#C0C0C0] cursor-grab active:cursor-grabbing" data-testid={`module-drag-handle-${group.id}`} />
                )}
                <button
                  onClick={() => setCollapsed(c => ({ ...c, [group.id]: !c[group.id] }))}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  data-testid={`toggle-module-${group.id}`}
                >
                  {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                  <span className="font-semibold text-[#111111] truncate">{group.name}</span>
                  <Badge variant="secondary" className="text-xs">{group.tasks.length}</Badge>
                </button>
              </div>

              {!isCollapsed && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                    {COLUMNS.map(col => {
                      const colTasks = group.tasks
                        .filter(t => t.status === col.status)
                        .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1));
                      const colCollapsed = isColumnCollapsed(group.id, col.status);
                      return (
                        <div
                          key={col.status}
                          onDragOver={(e) => { if (!draggedModuleId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                          onDrop={(e) => handleDrop(e, col.status)}
                          data-testid={`column-${group.id}-${col.status}`}
                        >
                          <div className="bg-[#F5F5F5] rounded-t-lg px-2 py-2 flex items-center justify-between gap-1">
                            <button
                              onClick={() => toggleColumn(group.id, col.status)}
                              className="flex items-center gap-1 min-w-0 hover:text-[#FF6B35] transition-colors"
                              data-testid={`toggle-column-${group.id}-${col.status}`}
                              title={colCollapsed ? 'Show tasks' : 'Hide tasks'}
                            >
                              {colCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                              <span className="font-medium text-xs text-[#111111] uppercase tracking-wide truncate">{col.label}</span>
                            </button>
                            <Badge variant="secondary" className="text-xs">{colTasks.length}</Badge>
                          </div>
                          {colCollapsed ? (
                            <div
                              className="bg-[#F5F5F5] rounded-b-lg p-2 min-h-[44px] flex items-center justify-center text-[11px] text-[#999999]"
                              data-testid={`column-collapsed-${group.id}-${col.status}`}
                            >
                              {colTasks.length > 0 ? `${colTasks.length} hidden — click arrow to show` : 'Empty'}
                            </div>
                          ) : (
                            <div className="bg-[#F5F5F5] rounded-b-lg p-2 min-h-[120px] space-y-2">
                              {colTasks.map(task => (
                                <TaskCard key={task.id} task={task} onClick={setModalTask} onDragStart={handleDragStart} onCopyLink={handleCopyLink} />
                              ))}
                              {colTasks.length === 0 && (
                                <div className="flex items-center justify-center h-16 text-xs text-[#999999]">Empty</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalTask !== undefined && (
        <DevTaskModal
          task={modalTask}
          modules={modules}
          users={assignableUsers}
          restaurants={restaurants}
          isAdmin={isAdmin}
          currentUser={user}
          onClose={() => { setModalTask(undefined); clearTaskParam(); }}
          onSaved={() => {
            setModalTask(undefined);
            clearTaskParam();
            fetchData();
          }}
        />
      )}
    </DashboardLayout>
  );
}
